import { Field, FormRenderer, Schema, useFieldApi, useFormApi } from '@data-driven-forms/react-form-renderer'
import { FormTemplateRenderProps } from '@data-driven-forms/react-form-renderer/common-types/form-template-render-props'
import componentTypes from '@data-driven-forms/react-form-renderer/component-types'
import { Input } from 'components/Forms'
import { useState } from 'react'
import { Button, ObModal } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { v4 as randomUuid } from 'uuid'

import { IColumnSetting, IDDFColumnSetting, IDDFField } from '../settingsTypes'

interface IProps {
  iddfType: IDDFColumnSetting
  handleAddColumnSetting: (newColumnSetting: IColumnSetting) => void
  title: JSX.Element
  columnSetting?: IColumnSetting
}

// this is called more than it should be but it does little harm - except to my pride (-:
const newColumnSetting = (iddfColumnSetting: IDDFColumnSetting) => {
  const fresh = {
    uuid: randomUuid(),
    sequence: Number.MAX_SAFE_INTEGER,
    type: iddfColumnSetting.type,
    description: 'new ' + iddfColumnSetting.description,
    typeSpecificConfiguration: {},
  } as IColumnSetting
  iddfColumnSetting.fields.forEach((iddfField) => (fresh.typeSpecificConfiguration[iddfField.name] = iddfField.name))
  return fresh
}

export const SettingsAddEditColumnSettingModal = ({
  iddfType,
  handleAddColumnSetting,
  title,
  columnSetting: columnSettingInitialValues,
}: IProps) => {
  if (!columnSettingInitialValues) columnSettingInitialValues = newColumnSetting(iddfType)
  const [show, setShow] = useState(false)
  const columnSetting = columnSettingInitialValues

  /**
   * @handleSubmitCallback
   * This we need to change. Currently we are changing only popup design as per system design. But the way this modal is created won't be able to accommodate the system design.
   * Because of this reason we are handing callback this way. We can change this in the future as this is not part of the scope for the design ticket
   */
  let handleSubmitCallback: any

  // see https://medium.com/@marvusm.mmi/custom-ddf-component-mapper-67850b88f511 (sandbox is useful)
  //{ schema:Schema, formFields:ElementType[] }
  const FormTemplate = (ftrProps: FormTemplateRenderProps) => {
    const { handleSubmit } = useFormApi()
    handleSubmitCallback = handleSubmit
    return (
      <>
        <h2>{ftrProps.schema.title}</h2>
        {ftrProps.formFields}
        <hr />
      </>
    )
  }
  const TextField = (props: any) => {
    const { input, /*meta,*/ ...rest } = useFieldApi(props)
    return (
      <div className="ddf-field">
        <Input {...rest} {...input} />
      </div>
    )
  }

  const TextArea = (props: any) => {
    const { name, label, input, /*meta,*/ ...rest } = useFieldApi(props)
    return (
      <div className="ddf-field">
        <label htmlFor={name} className="labelText">
          {label}
        </label>
        <textarea className="ddf-text-area" {...rest} {...input} />
      </div>
    )
  }
  const componentMapper = {
    [componentTypes.TEXT_FIELD]: TextField,
    [componentTypes.TEXTAREA]: TextArea,
  }

  const onSubmit = (values: Record<string, any>) => {
    const record: Record<string, string> = {}
    for (const key in values) {
      if (!key.startsWith('_ ')) record[key] = values[key]
    }
    columnSetting.description = values['_ description']
    columnSetting.typeSpecificConfiguration = record
    handleAddColumnSetting(columnSetting)
  }

  const schema: Schema = {
    fields: [
      {
        component: componentTypes.TEXT_FIELD,
        name: '_ description',
        label: 'Description',
        description: 'Describe for yourself what this rule does',
        initialValue: columnSetting.description,
      },
      ...iddfType.fields.map((iddfField: IDDFField) => {
        const field: Field = {
          component: iddfField.editComponent,
          name: iddfField.name,
          label: iddfField.description,
          description: iddfField.description,
          initialValue: columnSetting.typeSpecificConfiguration[iddfField.name],
        }
        return field
      }),
    ],
    //title: "Create or Edit Column Setting"
  }

  // const hasValidData = true
  return (
    <div>
      <Button
        color="info"
        size="small"
        onClick={(e: any) => {
          e.preventDefault()
          setShow(true)
        }}
      >
        {title}
      </Button>

      <ObModal
        show={show}
        close={() => setShow(false)}
        size="lg"
        title={'Create or Edit Column Setting'}
        height="auto"
        modalFooter={
          <FooterStyle>
            <Button
              variant="secondary"
              radius="rounded"
              size="large"
              width="100%"
              onClick={() => {
                setShow(false)
                handleSubmitCallback()
              }}
              // disabled={!hasValidData}
              className="take-ownership-btn"
            >
              Save Column Setting
            </Button>

            <Button variant="primary" radius="rounded" size="large" width="100%" onClick={() => setShow(false)}>
              No, cancel
            </Button>
          </FooterStyle>
        }
      >
        <InnerContainer>
          <p>File Center will use these settings to customize your file uploads.</p>
          <hr />
          <h3>{iddfType.description}</h3>
          <p>{iddfType.explanation}</p>
          <section className="input-container">
            <FormRenderer
              schema={schema}
              onSubmit={onSubmit}
              FormTemplate={FormTemplate}
              componentMapper={componentMapper}
            />
          </section>
        </InnerContainer>
      </ObModal>
    </div>
  )
}

const FooterStyle = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  width: -webkit-fill-available;
`

const InnerContainer = styled.div`
  p {
    font-size: ${({ theme }) => theme.util.pixelToRem(13)};
    color: ${({ theme }) => theme.colors.grey.darker};
  }

  .notice-container {
    margin-top: 1rem;
    margin-bottom: 1.5rem;
    padding: 0.25rem 0.5rem;
    background-color: ${({ theme }) => theme.colors.warning.lightest};
    border: 1px solid ${({ theme }) => theme.colors.warning.light};
    border-radius: ${({ theme }) => theme.borderRadius.normal};
    .notice-toggler {
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      span:first-child {
        color: ${({ theme }) => theme.colors.warning.darker};
        font-size: ${({ theme }) => theme.typography.xxs};
        font-weight: ${({ theme }) => theme.fontWeight.bold};
        svg {
          position: relative;
          bottom: 1px;
          color: ${({ theme }) => theme.colors.warning.dark};
        }
      }
      span:last-child {
        color: ${({ theme }) => theme.colors.warning.darker};
      }
    }
    #notice-collapse {
      p {
        color: ${({ theme }) => theme.colors.warning.darker};
        font-size: ${({ theme }) => theme.typography.xxs};
      }
    }
  }

  .input-container {
    margin-bottom: ${({ theme }) => theme.util.pixelToRem(36)};
    input {
      outline: 1px solid ${({ theme }) => theme.colors.primary.lighter};
    }
    .phone-number-container {
      input {
        width: ${({ theme }) => theme.util.pixelToRem(226)};
      }
    }
  }
  .ddf-field {
    margin-top: 1rem;
  }
  .labelText {
    text-align: left;
    ${({ theme }) => theme.typography.m.normal};
    padding-bottom: 0.25rem;
    color: ${({ theme }) => theme.colors.grey.darker};
  }
  .ddf-text-area {
    padding: 0.5rem;
    height: 4.5rem;
    width: 100%;
    resize: none;
  }
`
